import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react';
import {
  buttonRecipe,
  dialogSlotRecipe,
  drawerSlotRecipe,
  headingRecipe,
  inputRecipe,
  linkRecipe,
  menuSlotRecipe,
  nativeSelectSlotRecipe,
  selectSlotRecipe,
  tableSlotRecipe,
  textareaRecipe,
  tooltipSlotRecipe,
} from '@chakra-ui/react/theme';

/**
 * Apple Liquid Glass Theme
 *
 * Inspired by Apple's visionOS and iOS liquid glass design language.
 * Features translucent surfaces, soft luminous gradients, and ethereal depth.
 */

/** Stronger default than Chakra’s `semibold` so page titles read clearly on glass surfaces */
const headingRecipeBold = defineRecipe({
  ...headingRecipe,
  base: {
    ...headingRecipe.base,
    fontWeight: 'bold',
  },
});

const keyboardFocusState = {
  outline: '1px solid var(--attune-accent-focus-ring, currentColor)',
  outlineOffset: '1px',
  boxShadow: 'none',
};

const controlTransition =
  'background-color 150ms ease, border-color 150ms ease, color 150ms ease, opacity 150ms ease, transform 120ms ease';

const buttonRecipeAttune = defineRecipe({
  ...buttonRecipe,
  base: {
    ...buttonRecipe.base,
    borderRadius: 'glassInput',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    flexShrink: '0',
    transition: controlTransition,
    _focusVisible: keyboardFocusState,
    _active: {
      transform: 'translateY(1px)',
    },
  },
  variants: {
    size: {
      ...buttonRecipe.variants?.size,
      '2xs': { ...buttonRecipe.variants?.size?.['2xs'], paddingInline: '2.5' },
      xs: { ...buttonRecipe.variants?.size?.xs, paddingInline: '3' },
      sm: { ...buttonRecipe.variants?.size?.sm, paddingInline: '4' },
      md: { ...buttonRecipe.variants?.size?.md, paddingInline: '5' },
      lg: { ...buttonRecipe.variants?.size?.lg, paddingInline: '6' },
      xl: { ...buttonRecipe.variants?.size?.xl, paddingInline: '6' },
      '2xl': { ...buttonRecipe.variants?.size?.['2xl'], paddingInline: '8' },
    },
    variant: buttonRecipe.variants!.variant!,
  },
});

const linkRecipeAttune = defineRecipe({
  ...linkRecipe,
  base: {
    ...linkRecipe.base,
    borderRadius: 'sm',
    transition: 'color 150ms ease, background-color 150ms ease, text-decoration-color 150ms ease',
    _focusVisible: keyboardFocusState,
  },
});

const inputRecipeAttune = defineRecipe({
  ...inputRecipe,
  base: {
    ...inputRecipe.base,
    bg: 'ui.surface.input',
    color: 'ui.text.primary',
    borderColor: 'ui.border.default',
    borderRadius: 'glassInput',
    caretColor: 'ui.text.accent',
    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    _placeholder: {
      color: 'ui.text.tertiary',
      opacity: 1,
    },
    _focusVisible: {
      borderColor: 'ui.border.accent',
      ...keyboardFocusState,
    },
  },
});

const textareaRecipeAttune = defineRecipe({
  ...textareaRecipe,
  base: {
    ...textareaRecipe.base,
    bg: 'ui.surface.input',
    color: 'ui.text.primary',
    borderColor: 'ui.border.default',
    borderRadius: 'glassInput',
    caretColor: 'ui.text.accent',
    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    _placeholder: {
      color: 'ui.text.tertiary',
      opacity: 1,
    },
    _focusVisible: {
      borderColor: 'ui.border.accent',
      ...keyboardFocusState,
    },
  },
});

const dialogSlotRecipeAttune = defineSlotRecipe({
  ...dialogSlotRecipe,
  base: {
    ...dialogSlotRecipe.base,
    backdrop: {
      ...dialogSlotRecipe.base?.backdrop,
      bg: 'ui.overlay.backdrop',
      backdropFilter: 'blur(6px)',
    },
    positioner: {
      ...dialogSlotRecipe.base?.positioner,
      p: { base: '3', md: '6' },
      overscrollBehavior: 'contain',
    },
    content: {
      ...dialogSlotRecipe.base?.content,
      bg: 'ui.surface.popover',
      color: 'ui.text.primary',
      borderRadius: 'glassCard',
      borderWidth: '1px',
      borderColor: 'ui.border.default',
      boxShadow: 'var(--attune-shadow-floating, none)',
      maxH: 'calc(100dvh - 2rem)',
      overflow: 'hidden',
    },
    header: {
      ...dialogSlotRecipe.base?.header,
      alignItems: 'center',
      px: '5',
      pt: '5',
      pb: '4',
      borderBottomWidth: '1px',
      borderColor: 'ui.border.subtle',
    },
    body: {
      ...dialogSlotRecipe.base?.body,
      px: '5',
      py: '5',
      minH: '0',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
    footer: {
      ...dialogSlotRecipe.base?.footer,
      px: '5',
      py: '4',
      gap: '3',
      flexWrap: 'wrap',
      borderTopWidth: '1px',
      borderColor: 'ui.border.subtle',
      bg: 'ui.surface.input',
      '& > .chakra-stack': {
        minW: '0',
        flexWrap: 'wrap',
        rowGap: '3',
        columnGap: '3',
      },
    },
    title: {
      ...dialogSlotRecipe.base?.title,
      color: 'ui.text.primary',
      fontSize: 'md',
      fontWeight: '700',
      lineHeight: '1.3',
      textWrap: 'balance',
    },
    description: {
      ...dialogSlotRecipe.base?.description,
      color: 'ui.text.secondary',
      lineHeight: '1.55',
    },
    closeTrigger: {
      ...dialogSlotRecipe.base?.closeTrigger,
      top: '3',
      insetEnd: '3',
    },
  },
  defaultVariants: {
    ...dialogSlotRecipe.defaultVariants,
    placement: 'center',
    scrollBehavior: 'inside',
  },
});

const drawerSlotRecipeAttune = defineSlotRecipe({
  ...drawerSlotRecipe,
  base: {
    ...drawerSlotRecipe.base,
    backdrop: {
      ...drawerSlotRecipe.base?.backdrop,
      bg: 'ui.overlay.backdrop',
      backdropFilter: 'blur(6px)',
    },
    positioner: {
      ...drawerSlotRecipe.base?.positioner,
      overscrollBehavior: 'contain',
    },
    content: {
      ...drawerSlotRecipe.base?.content,
      bg: 'ui.surface.drawer',
      color: 'ui.text.primary',
      borderColor: 'ui.border.default',
      borderWidth: '1px',
      boxShadow: 'var(--attune-shadow-floating, none)',
    },
    header: {
      ...drawerSlotRecipe.base?.header,
      minH: '64px',
      px: '5',
      py: '4',
      borderBottomWidth: '1px',
      borderColor: 'ui.border.subtle',
      bg: 'ui.surface.drawerHeader',
    },
    body: {
      ...drawerSlotRecipe.base?.body,
      px: '5',
      py: '4',
      overscrollBehavior: 'contain',
    },
    footer: {
      ...drawerSlotRecipe.base?.footer,
      px: '5',
      py: '4',
      gap: '3',
      flexWrap: 'wrap',
      borderTopWidth: '1px',
      borderColor: 'ui.border.subtle',
      bg: 'ui.surface.drawerHeader',
      '& > .chakra-stack': {
        minW: '0',
        flexWrap: 'wrap',
        rowGap: '3',
        columnGap: '3',
      },
    },
    title: {
      ...drawerSlotRecipe.base?.title,
      color: 'ui.text.primary',
      fontSize: 'md',
      fontWeight: '700',
    },
    description: {
      ...drawerSlotRecipe.base?.description,
      color: 'ui.text.secondary',
    },
  },
});

const nativeSelectSlotRecipeAttune = defineSlotRecipe({
  ...nativeSelectSlotRecipe,
  base: {
    ...nativeSelectSlotRecipe.base,
    field: {
      ...nativeSelectSlotRecipe.base?.field,
      bg: 'ui.surface.input',
      color: 'ui.text.primary',
      borderColor: 'ui.border.default',
      borderRadius: 'glassInput',
      transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
      _focusVisible: {
        borderColor: 'ui.border.accent',
        ...keyboardFocusState,
      },
      '& > option, & > optgroup': {
        bg: 'ui.surface.popover',
        color: 'ui.text.primary',
      },
    },
    indicator: {
      ...nativeSelectSlotRecipe.base?.indicator,
      color: 'ui.text.secondary',
    },
  },
  variants: {
    variant: {
      ...nativeSelectSlotRecipe.variants?.variant,
      outline: {
        field: {
          bg: 'ui.surface.input',
          borderWidth: '1px',
          borderColor: 'ui.border.default',
          _hover: { borderColor: 'ui.border.accent' },
          _expanded: { borderColor: 'ui.border.accent' },
        },
      },
      subtle: {
        field: {
          bg: 'ui.surface.input',
          borderWidth: '1px',
          borderColor: 'ui.border.subtle',
          _hover: { borderColor: 'ui.border.default' },
        },
      },
    },
    size: nativeSelectSlotRecipe.variants!.size!,
  },
});

const floatingSurface = {
  bg: 'ui.surface.popover',
  color: 'ui.text.primary',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'ui.border.default',
  borderRadius: 'glassInput',
  boxShadow: 'var(--attune-shadow-floating, none)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
  overscrollBehavior: 'contain',
} as const;

const menuSlotRecipeAttune = defineSlotRecipe({
  ...menuSlotRecipe,
  base: {
    ...menuSlotRecipe.base,
    content: {
      ...menuSlotRecipe.base?.content,
      ...floatingSurface,
      p: '1.5',
    },
    item: {
      ...menuSlotRecipe.base?.item,
      minH: '34px',
      color: 'ui.text.secondary',
      borderRadius: 'glassInput',
      transition: 'background-color 120ms ease, color 120ms ease',
      _hover: {
        bg: 'ui.surface.rowHover',
        color: 'ui.text.primary',
      },
      _highlighted: {
        bg: 'ui.surface.rowHover',
        color: 'ui.text.primary',
      },
      _focusVisible: keyboardFocusState,
    },
    itemGroupLabel: {
      ...menuSlotRecipe.base?.itemGroupLabel,
      color: 'ui.text.tertiary',
      fontSize: 'xs',
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    itemCommand: {
      ...menuSlotRecipe.base?.itemCommand,
      color: 'ui.text.tertiary',
      opacity: 1,
    },
    separator: {
      ...menuSlotRecipe.base?.separator,
      bg: 'ui.border.subtle',
    },
  },
  variants: {
    variant: {
      ...menuSlotRecipe.variants?.variant,
      subtle: {
        item: {
          _highlighted: {
            bg: 'ui.surface.rowHover',
            color: 'ui.text.primary',
          },
        },
      },
    },
    size: menuSlotRecipe.variants!.size!,
  },
});

const selectSlotRecipeAttune = defineSlotRecipe({
  ...selectSlotRecipe,
  base: {
    ...selectSlotRecipe.base,
    trigger: {
      ...selectSlotRecipe.base?.trigger,
      bg: 'ui.surface.input',
      color: 'ui.text.primary',
      borderWidth: '1px',
      borderColor: 'ui.border.default',
      borderRadius: 'glassInput',
      transition: controlTransition,
      _hover: {
        borderColor: 'ui.border.accent',
        bg: 'ui.surface.selected',
      },
      _expanded: {
        borderColor: 'ui.border.accent',
        bg: 'ui.surface.selected',
      },
      _focusVisible: {
        borderColor: 'ui.border.accent',
        ...keyboardFocusState,
      },
      _placeholderShown: {
        color: 'ui.text.tertiary',
      },
    },
    indicator: {
      ...selectSlotRecipe.base?.indicator,
      color: 'ui.text.secondary',
    },
    content: {
      ...selectSlotRecipe.base?.content,
      ...floatingSurface,
      p: '1.5',
    },
    item: {
      ...selectSlotRecipe.base?.item,
      minH: '34px',
      color: 'ui.text.secondary',
      borderRadius: 'glassInput',
      transition: 'background-color 120ms ease, color 120ms ease',
      _hover: {
        bg: 'ui.surface.rowHover',
        color: 'ui.text.primary',
      },
      _highlighted: {
        bg: 'ui.surface.rowHover',
        color: 'ui.text.primary',
      },
      _checked: {
        bg: 'ui.surface.selected',
        color: 'ui.text.primary',
        fontWeight: '600',
      },
      _focusVisible: keyboardFocusState,
    },
    itemIndicator: {
      ...selectSlotRecipe.base?.itemIndicator,
      color: 'ui.text.accent',
    },
    itemGroupLabel: {
      ...selectSlotRecipe.base?.itemGroupLabel,
      color: 'ui.text.tertiary',
      fontSize: 'xs',
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },
  variants: {
    variant: {
      ...selectSlotRecipe.variants?.variant,
      outline: {
        trigger: {
          bg: 'ui.surface.input',
          borderWidth: '1px',
          borderColor: 'ui.border.default',
          _hover: { borderColor: 'ui.border.accent', bg: 'ui.surface.selected' },
          _expanded: { borderColor: 'ui.border.accent', bg: 'ui.surface.selected' },
        },
      },
      subtle: {
        trigger: {
          bg: 'ui.surface.input',
          borderWidth: '1px',
          borderColor: 'ui.border.subtle',
          _hover: { borderColor: 'ui.border.default', bg: 'ui.surface.selected' },
        },
      },
    },
    size: selectSlotRecipe.variants!.size!,
  },
});

const tooltipSlotRecipeAttune = defineSlotRecipe({
  ...tooltipSlotRecipe,
  base: {
    ...tooltipSlotRecipe.base,
    content: {
      ...tooltipSlotRecipe.base?.content,
      '--tooltip-bg': 'colors.data.tooltipBg',
      ...floatingSurface,
      bg: 'var(--tooltip-bg)',
      px: '3',
      py: '2',
      maxW: '320px',
      fontWeight: '500',
      lineHeight: '1.45',
    },
    arrow: {
      ...tooltipSlotRecipe.base?.arrow,
      '--arrow-background': 'var(--tooltip-bg)',
    },
    arrowTip: {
      ...tooltipSlotRecipe.base?.arrowTip,
      borderColor: 'ui.border.default',
    },
  },
});

const tableSlotRecipeAttune = defineSlotRecipe({
  ...tableSlotRecipe,
  base: {
    ...tableSlotRecipe.base,
    root: {
      ...tableSlotRecipe.base?.root,
      color: 'ui.text.primary',
      fontVariantNumeric: 'tabular-nums',
    },
    header: {
      ...tableSlotRecipe.base?.header,
      bg: 'ui.surface.input',
    },
    row: {
      ...tableSlotRecipe.base?.row,
      bg: 'transparent',
      borderColor: 'ui.border.subtle',
      transition: 'background-color 120ms ease',
    },
    columnHeader: {
      ...tableSlotRecipe.base?.columnHeader,
      color: 'ui.text.secondary',
      fontSize: 'xs',
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    cell: {
      ...tableSlotRecipe.base?.cell,
      borderColor: 'ui.border.subtle',
    },
  },
});

const interactiveControlSelectors = [
  'button',
  'a',
  '[role="button"]',
  '[type="button"]',
  '[type="submit"]',
  '[type="reset"]',
  '.chakra-button',
  '.chakra-link',
  '[data-scope="button"]',
  '[data-part="trigger"]',
  '[data-part="item"]',
  '[data-part="control"]',
].join(', ');

const interactiveControlFocusSelectors = [
  'button',
  'a',
  '[role="button"]',
  '[type="button"]',
  '[type="submit"]',
  '[type="reset"]',
  '.chakra-button',
  '.chakra-link',
  '[data-scope="button"]',
  '[data-part="trigger"]',
  '[data-part="item"]',
  '[data-part="control"]',
]
  .map(
    selector =>
      `${selector}:not([data-focus-style="inset"]):is(:focus-visible, [data-focus-visible])`
  )
  .join(', ');

const appBackdropImage = `
  radial-gradient(ellipse 140% 70% at 50% -10%, var(--attune-app-ambient-glow, rgba(255, 255, 255, 0.55)), transparent 52%),
  linear-gradient(165deg, var(--chakra-colors-app-bg-wash) 0%, var(--chakra-colors-app-bg) 42%, var(--chakra-colors-app-bg-depth) 100%)
`;

const config = defineConfig({
  theme: {
    tokens: {
      fonts: {
        heading: {
          value:
            'var(--font-sans), "Manrope", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        body: {
          value:
            'var(--font-sans), "Manrope", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
      },
      colors: {
        // Liquid Glass Base Colors
        glass: {
          white: { value: 'rgba(255, 255, 255, 0.72)' },
          light: { value: 'rgba(255, 255, 255, 0.60)' },
          subtle: { value: 'rgba(255, 255, 255, 0.45)' },
          mist: { value: 'rgba(255, 255, 255, 0.12)' },
          dark: { value: 'rgba(0, 0, 0, 0.35)' },
          darkSubtle: { value: 'rgba(0, 0, 0, 0.18)' },
          darkMist: { value: 'rgba(0, 0, 0, 0.08)' },
        },
        // Luminous Accent Colors (legacy names; blue resolves to the active graphite accent)
        luminous: {
          blue: { value: 'var(--attune-luminous-blue, #171717)' },
          blueGlow: { value: 'var(--attune-luminous-blue-glow, rgba(23, 23, 23, 0.24))' },
          purple: { value: 'var(--attune-luminous-purple, #AF52DE)' },
          purpleGlow: { value: 'var(--attune-luminous-purple-glow, rgba(175, 82, 222, 0.35))' },
          pink: { value: 'var(--attune-luminous-pink, #FF2D55)' },
          pinkGlow: { value: 'var(--attune-luminous-pink-glow, rgba(255, 45, 85, 0.35))' },
          orange: { value: 'var(--attune-luminous-orange, #FF9500)' },
          orangeGlow: { value: 'var(--attune-luminous-orange-glow, rgba(255, 149, 0, 0.35))' },
          green: { value: 'var(--attune-luminous-green, #34C759)' },
          greenGlow: { value: 'var(--attune-luminous-green-glow, rgba(52, 199, 89, 0.35))' },
          teal: { value: 'var(--attune-luminous-teal, #5AC8FA)' },
          tealGlow: { value: 'var(--attune-luminous-teal-glow, rgba(90, 200, 250, 0.35))' },
          cyan: { value: 'var(--attune-luminous-cyan, #32D4DE)' },
          cyanGlow: { value: 'var(--attune-luminous-cyan-glow, rgba(50, 212, 222, 0.35))' },
          indigo: { value: 'var(--attune-luminous-indigo, #5856D6)' },
          indigoGlow: { value: 'var(--attune-luminous-indigo-glow, rgba(88, 86, 214, 0.35))' },
        },
        // Surface & Background Colors
        surface: {
          primary: { value: '#F5F5F7' },
          secondary: { value: '#FBFBFD' },
          tertiary: { value: '#FFFFFF' },
          ambient: { value: '#E8E8ED' },
          mesh1: { value: '#C7D2FE' }, // Soft indigo for mesh gradients
          mesh2: { value: '#FBCFE8' }, // Soft pink
          mesh3: { value: '#A5F3FC' }, // Soft cyan
          mesh4: { value: '#D9F99D' }, // Soft lime
        },
        // Text Colors — secondary/tertiary bumped for legibility on light gray mesh backgrounds
        ink: {
          primary: { value: 'rgba(0, 0, 0, 0.88)' },
          secondary: { value: 'rgba(0, 0, 0, 0.64)' },
          tertiary: { value: 'rgba(0, 0, 0, 0.48)' },
          inverted: { value: 'rgba(255, 255, 255, 0.92)' },
          invertedSecondary: { value: 'rgba(255, 255, 255, 0.74)' },
        },
        // Border Colors
        edge: {
          light: { value: 'rgba(255, 255, 255, 0.65)' },
          subtle: { value: 'rgba(255, 255, 255, 0.35)' },
          dark: { value: 'rgba(0, 0, 0, 0.08)' },
          darkSubtle: { value: 'rgba(0, 0, 0, 0.04)' },
          glow: { value: 'rgba(255, 255, 255, 0.85)' },
        },
        // Your existing brand colors (preserved)
        ablack: { value: '#030303' },
        agreen: { value: '#DCFEC8' },
        agray: { value: '#C0C0C0' },
        alightgray: { value: '#F2F2F2' },
        brandBlue: { value: '#34B6FF' },
        brandOrange: { value: '#FF3F00' },
        brandGreen: { value: '#419D3A' },
        brandPink: { value: '#FF679F' },
        brandGray: { value: '#C6C6C9' },
        brandGrayLight: { value: '#E3E3E4' },
        neonGreen: { value: '#39FF14' },
        neonPink: { value: '#FF00FF' },
        neonCyan: { value: '#00FFFF' },
        neonPurple: { value: '#D934FF' },
        neonYellow: { value: '#FFFF00' },
      },
      // Blur tokens for glass effects
      blurs: {
        glass: { value: '24px' },
        glassLight: { value: '16px' },
        glassSubtle: { value: '8px' },
        glassHeavy: { value: '40px' },
        glassExtreme: { value: '64px' },
      },
      // One restrained radius scale keeps data-dense surfaces and controls visually related.
      radii: {
        glass: { value: '18px' },
        glassLg: { value: '20px' },
        glassXl: { value: '24px' },
        glass2xl: { value: '28px' },
        glassPill: { value: '9999px' },
        glassCard: { value: '18px' },
        glassButton: { value: '12px' },
        glassInput: { value: '10px' },
      },
      // Focus rings only. Decorative surface elevation is intentionally flat.
      shadows: {
        glassElevated: {
          value: 'none',
        },
        glassFloating: {
          value: 'none',
        },
        glassPressed: {
          value: 'none',
        },
        glassInner: {
          value: 'none',
        },
        glassGlow: {
          value: 'none',
        },
        glassSubtle: { value: 'none' },
        glassRing: { value: 'none' },
      },
      // Animation durations
      durations: {
        glassHover: { value: '200ms' },
        glassPress: { value: '100ms' },
        glassTransition: { value: '300ms' },
        glassReveal: { value: '400ms' },
        glassMorph: { value: '500ms' },
      },
      // Easing functions
      easings: {
        glassIn: { value: 'cubic-bezier(0.32, 0, 0.67, 0)' },
        glassOut: { value: 'cubic-bezier(0.33, 1, 0.68, 1)' },
        glassInOut: { value: 'cubic-bezier(0.65, 0, 0.35, 1)' },
        glassSpring: { value: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
        glassBounce: { value: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)' },
      },
    },
    semanticTokens: {
      colors: {
        // Custom semantic tokens need a concrete `base` value so the root CSS vars
        // are never empty before color-mode selectors apply.
        // Primary glass backgrounds
        'glass.bg': {
          value: 'var(--attune-glass-bg, rgba(255, 255, 255, 0.72))',
        },
        'glass.bg.subtle': {
          value: 'var(--attune-glass-bg-subtle, rgba(255, 255, 255, 0.60))',
        },
        'glass.bg.muted': {
          value: 'var(--attune-glass-bg-muted, rgba(255, 255, 255, 0.45))',
        },
        /** Solid panel / drawer root (aligned with `surface` — used by drawers & rails) */
        'glass.bg.base': {
          value: 'var(--attune-glass-bg-base, #FBFBFD)',
        },
        /** Slightly lifted surface (selected rows, chips) */
        'glass.bg.elevated': {
          value: 'var(--attune-glass-bg-elevated, #FFFFFF)',
        },
        /** Card / control hover fill (research assistant, meters) */
        'glass.bg.card': {
          value: 'var(--attune-glass-bg-card, #FFFFFF)',
        },
        /** Muted glass tint for nested rows/cards (replaces raw `glass.mist` where theme-aware fill is needed) */
        'glass.fill': {
          value: 'var(--attune-glass-fill, rgba(255, 255, 255, 0.12))',
        },
        /** Row hover on glass lists (citations, links) */
        'glass.interactiveHover': {
          value: 'var(--attune-glass-interactive-hover, rgba(0, 0, 0, 0.08))',
        },
        /** Very light press/hover on expandable trace headers */
        'glass.traceHeaderHover': {
          value: 'var(--attune-glass-trace-header-hover, rgba(0, 0, 0, 0.03))',
        },
        // Glass borders — use literals so CSS vars always resolve (broken refs fall back to currentColor ≈ black)
        'glass.border': {
          value: 'var(--attune-glass-border, #E3E3E4)',
        },
        'glass.border.subtle': {
          value: 'var(--attune-glass-border-subtle, rgba(0, 0, 0, 0.06))',
        },
        // Ink/Text colors
        'glass.text': {
          value: 'var(--attune-ui-text-primary, rgba(0, 0, 0, 0.88))',
        },
        'glass.text.primary': {
          value: 'var(--attune-ui-text-primary, rgba(0, 0, 0, 0.88))',
        },
        'glass.text.secondary': {
          value: 'var(--attune-ui-text-secondary, rgba(0, 0, 0, 0.64))',
        },
        'glass.text.tertiary': {
          value: 'var(--attune-ui-text-tertiary, rgba(0, 0, 0, 0.48))',
        },
        'glass.text.muted': {
          value: 'var(--attune-ui-text-tertiary, rgba(0, 0, 0, 0.48))',
        },
        // Primary accent (liquid-glass graphite)
        accent: {
          solid: { value: 'var(--attune-accent-solid, #171717)' },
          contrast: { value: 'var(--attune-accent-on-solid, #FFFFFF)' },
          fg: { value: 'var(--attune-accent-fg, #171717)' },
          muted: { value: 'var(--attune-accent-muted, rgba(23, 23, 23, 0.24))' },
          subtle: { value: 'var(--attune-accent-subtle, rgba(23, 23, 23, 0.08))' },
          emphasized: { value: 'var(--attune-accent-emphasized, rgba(23, 23, 23, 0.16))' },
          focusRing: {
            value: 'var(--attune-accent-focus-ring, var(--attune-accent-fg, #171717))',
          },
          /** Small chips (e.g. sub-agent badge in tool traces) */
          chip: {
            value: 'var(--attune-accent-chip, rgba(23, 23, 23, 0.07))',
          },
          /** Expanded trace / soft accent panels */
          traceSurface: {
            value: 'var(--attune-accent-trace-surface, rgba(23, 23, 23, 0.035))',
          },
          traceSurfaceHover: {
            value: 'var(--attune-accent-trace-surface-hover, rgba(23, 23, 23, 0.06))',
          },
          traceBorder: {
            value: 'var(--attune-accent-trace-border, rgba(23, 23, 23, 0.12))',
          },
          traceBorderHover: {
            value: 'var(--attune-accent-trace-border-hover, rgba(23, 23, 23, 0.16))',
          },
          /** Answer/source link cards on hover */
          answerHoverBorder: {
            value: 'var(--attune-accent-answer-hover-border, rgba(23, 23, 23, 0.2))',
          },
          /** Secondary accent chips (source counts, indigo) */
          secondarySubtle: {
            value: 'var(--attune-accent-secondary-subtle, rgba(88, 86, 214, 0.08))',
          },
          /** Featured command / strong outline */
          commandBorder: {
            value: 'var(--attune-accent-command-border, rgba(23, 23, 23, 0.35))',
          },
        },
        success: {
          subtle: {
            value: {
              base: 'rgba(52, 199, 89, 0.1)',
              _dark: 'rgba(52, 199, 89, 0.2)',
            },
          },
        },
        // Your preserved brand semantic tokens
        brand: {
          blue: { value: '{colors.brandBlue}' },
          orange: { value: '{colors.brandOrange}' },
          green: { value: '{colors.brandGreen}' },
          pink: { value: '{colors.brandPink}' },
          gray: { value: '{colors.brandGray}' },
          grayLight: { value: '{colors.brandGrayLight}' },
          neonGreen: { value: '{colors.neonGreen}' },
          neonPink: { value: '{colors.neonPink}' },
          neonCyan: { value: '{colors.neonCyan}' },
          neonPurple: { value: '{colors.neonPurple}' },
          neonYellow: { value: '{colors.neonYellow}' },
        },
        theme: {
          ablack: { value: '{colors.ablack}' },
          agreen: { value: '{colors.agreen}' },
          agray: { value: '{colors.agray}' },
          alightgray: { value: '{colors.alightgray}' },
        },
        app: {
          bg: { value: 'var(--attune-app-bg, #FAF9F5)' },
          bgWash: { value: 'var(--attune-app-bg-wash, #FAF9F5)' },
          bgDepth: { value: 'var(--attune-app-bg-depth, #FAF9F5)' },
          meshDot: { value: 'var(--attune-app-mesh-dot, rgba(52, 44, 36, 0.22))' },
          ambientGlow: { value: 'var(--attune-app-ambient-glow, rgba(255, 255, 255, 0.55))' },
          accentStart: { value: 'var(--attune-accent-start, #171717)' },
          accentEnd: { value: 'var(--attune-accent-end, #3A3A3A)' },
          accentShadow: { value: 'var(--attune-accent-shadow, rgba(23, 23, 23, 0.18))' },
          accentShadowHover: { value: 'var(--attune-accent-shadow-hover, rgba(23, 23, 23, 0.26))' },
          scrollbarThumb: { value: 'var(--attune-scrollbar-thumb, rgba(0, 0, 0, 0.15))' },
          scrollbarThumbHover: {
            value: 'var(--attune-scrollbar-thumb-hover, rgba(0, 0, 0, 0.25))',
          },
          header: { value: 'var(--attune-ui-surface-card, rgba(255, 255, 255, 0.60))' },
          content: { value: 'var(--attune-ui-text-primary, rgba(0, 0, 0, 0.88))' },
        },
        public: {
          bg: { value: 'var(--attune-app-bg, #FAF9F5)' },
          fg: { value: 'var(--attune-ui-text-primary, rgba(0, 0, 0, 0.88))' },
          muted: { value: 'var(--attune-ui-text-secondary, rgba(0, 0, 0, 0.64))' },
          border: { value: 'var(--attune-ui-border-default, rgba(0, 0, 0, 0.16))' },
          accent: { value: 'var(--attune-accent-solid, #171717)' },
          accentText: { value: 'var(--attune-accent-on-solid, #FFFFFF)' },
          panel: { value: 'var(--attune-ui-surface-card, rgba(255, 255, 255, 0.60))' },
        },
        ui: {
          surface: {
            panel: { value: 'var(--attune-ui-surface-panel, rgba(255, 255, 255, 0.60))' },
            card: { value: 'var(--attune-ui-surface-card, rgba(255, 255, 255, 0.60))' },
            rail: { value: 'var(--attune-ui-surface-rail, rgba(255, 255, 255, 0.45))' },
            popover: { value: 'var(--attune-ui-surface-popover, rgba(255, 255, 255, 0.72))' },
            drawer: { value: 'var(--attune-ui-surface-drawer, #FFFFFF)' },
            drawerHeader: { value: 'var(--attune-ui-surface-drawer-header, #FBFBFA)' },
            drawerMuted: { value: 'var(--attune-ui-surface-drawer-muted, #F7F7F5)' },
            input: { value: 'var(--attune-ui-surface-input, rgba(255, 255, 255, 0.45))' },
            row: { value: { base: 'transparent', _dark: 'transparent' } },
            rowHover: {
              value: 'var(--attune-ui-surface-row-hover, rgba(0, 0, 0, 0.08))',
            },
            selected: {
              value: 'var(--attune-ui-surface-selected, #FFFFFF)',
            },
            warning: {
              value: 'var(--attune-ui-surface-warning, rgba(255, 149, 0, 0.1))',
            },
          },
          border: {
            default: { value: 'var(--attune-ui-border-default, #E3E3E4)' },
            subtle: { value: 'var(--attune-ui-border-subtle, rgba(0, 0, 0, 0.06))' },
            divider: {
              value:
                'color-mix(in srgb, var(--attune-ui-border-subtle, rgba(0, 0, 0, 0.06)) 55%, transparent)',
            },
            strong: { value: 'var(--attune-ui-border-strong, rgba(0, 0, 0, 0.16))' },
            accent: {
              value: 'var(--attune-ui-border-accent, rgba(23, 23, 23, 0.28))',
            },
          },
          text: {
            primary: { value: 'var(--attune-ui-text-primary, rgba(0, 0, 0, 0.88))' },
            secondary: {
              value: 'var(--attune-ui-text-secondary, rgba(0, 0, 0, 0.64))',
            },
            tertiary: {
              value: 'var(--attune-ui-text-tertiary, rgba(0, 0, 0, 0.48))',
            },
            inverse: { value: '{colors.ink.inverted}' },
            accent: { value: 'var(--attune-ui-text-accent, #171717)' },
          },
          overlay: {
            backdrop: { value: { base: 'rgba(0, 0, 0, 0.28)', _dark: 'rgba(0, 0, 0, 0.42)' } },
          },
        },
        status: {
          success: {
            fg: { value: 'var(--attune-status-success-fg, #1F8F3A)' },
            bg: { value: 'var(--attune-status-success-bg, rgba(52, 199, 89, 0.11))' },
            border: { value: 'var(--attune-status-success-border, rgba(52, 199, 89, 0.26))' },
          },
          warning: {
            fg: { value: 'var(--attune-status-warning-fg, #B66200)' },
            bg: { value: 'var(--attune-status-warning-bg, rgba(255, 149, 0, 0.13))' },
            border: { value: 'var(--attune-status-warning-border, rgba(255, 149, 0, 0.3))' },
          },
          danger: {
            fg: { value: 'var(--attune-status-danger-fg, #D83B3B)' },
            bg: { value: 'var(--attune-status-danger-bg, rgba(255, 45, 85, 0.11))' },
            border: { value: 'var(--attune-status-danger-border, rgba(255, 45, 85, 0.28))' },
          },
          info: {
            fg: { value: 'var(--attune-status-info-fg, #171717)' },
            bg: { value: 'var(--attune-status-info-bg, rgba(23, 23, 23, 0.07))' },
            border: { value: 'var(--attune-status-info-border, rgba(23, 23, 23, 0.16))' },
          },
          neutral: {
            fg: { value: 'var(--attune-status-neutral-fg, rgba(0, 0, 0, 0.64))' },
            bg: { value: 'var(--attune-status-neutral-bg, rgba(0, 0, 0, 0.045))' },
            border: { value: 'var(--attune-status-neutral-border, rgba(0, 0, 0, 0.08))' },
          },
        },
        rank: {
          gold: {
            fg: { value: 'var(--attune-rank-gold-fg, #B7791F)' },
            bg: { value: 'var(--attune-rank-gold-bg, rgba(251, 191, 36, 0.18))' },
            border: { value: 'var(--attune-rank-gold-border, rgba(251, 191, 36, 0.44))' },
          },
          silver: {
            fg: { value: 'var(--attune-rank-silver-fg, #64748B)' },
            bg: { value: 'var(--attune-rank-silver-bg, rgba(148, 163, 184, 0.15))' },
            border: { value: 'var(--attune-rank-silver-border, rgba(148, 163, 184, 0.36))' },
          },
          bronze: {
            fg: { value: 'var(--attune-rank-bronze-fg, #B46A2D)' },
            bg: { value: 'var(--attune-rank-bronze-bg, rgba(180, 120, 60, 0.15))' },
            border: { value: 'var(--attune-rank-bronze-border, rgba(180, 120, 60, 0.34))' },
          },
        },
        data: {
          /** Chart series colors are theme variables so data pages follow the selected design preset. */
          visibility: { value: 'var(--attune-data-visibility, #171717)' },
          rank3: { value: 'var(--attune-data-rank3, #D69E2E)' },
          rank1: { value: 'var(--attune-data-rank1, #38A169)' },
          missed: { value: 'var(--attune-data-missed, #EF4444)' },
          positive: { value: 'var(--attune-data-positive, #34C759)' },
          negative: { value: 'var(--attune-data-negative, #FF3B30)' },
          neutral: { value: 'var(--attune-data-neutral, #A0AEC0)' },
          orange: { value: 'var(--attune-data-orange, #FF7900)' },
          purple: { value: 'var(--attune-data-purple, #8B5CF6)' },
          cyan: { value: 'var(--attune-data-cyan, #06B6D4)' },
          pink: { value: 'var(--attune-data-pink, #EC4899)' },
          tooltipBg: {
            value: 'var(--attune-data-tooltip-bg, rgba(255, 255, 255, 0.97))',
          },
          grid: {
            value: 'var(--attune-data-grid, rgba(0, 0, 0, 0.05))',
          },
          axis: {
            value: 'var(--attune-data-axis, #6B7280)',
          },
          cursor: {
            value: 'var(--attune-data-cursor, rgba(0, 0, 0, 0.08))',
          },
        },
        chart: {
          series: {
            1: { value: 'var(--attune-chart-series-1, #171717)' },
            2: { value: 'var(--attune-chart-series-2, #AF52DE)' },
            3: { value: 'var(--attune-chart-series-3, #FF9500)' },
            4: { value: 'var(--attune-chart-series-4, #34C759)' },
            5: { value: 'var(--attune-chart-series-5, #FF2D55)' },
            6: { value: 'var(--attune-chart-series-6, #32D4DE)' },
            7: { value: 'var(--attune-chart-series-7, #5856D6)' },
            8: { value: 'var(--attune-chart-series-8, #FF7900)' },
            9: { value: 'var(--attune-chart-series-9, #06B6D4)' },
            10: { value: 'var(--attune-chart-series-10, #8B5CF6)' },
            11: { value: 'var(--attune-chart-series-11, #D69E2E)' },
            12: { value: 'var(--attune-chart-series-12, #38A169)' },
            13: { value: 'var(--attune-chart-series-13, #EC4899)' },
            14: { value: 'var(--attune-chart-series-14, #64748B)' },
            15: { value: 'var(--attune-chart-series-15, #B46A2D)' },
          },
        },
      },
      /** Chakra nested radii inherit Attune's shared surface/control scale. */
      radii: {
        l1: { value: '{radii.glassCard}' },
        l2: { value: '{radii.glassInput}' },
        l3: { value: '{radii.glassCard}' },
      },
    },
    // Layer styles for glass surfaces
    layerStyles: {
      // Primary glass panel
      glass: {
        value: {
          background: 'glass.bg',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glass',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border',
          boxShadow: 'none',
        },
      },
      // Subtle glass (lighter opacity)
      glassSubtle: {
        value: {
          background: 'glass.bg.subtle',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glass',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border.subtle',
          boxShadow: 'none',
        },
      },
      // Elevated glass (floating elements)
      glassFloating: {
        value: {
          background: 'glass.bg.subtle',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          borderRadius: 'glassLg',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border',
          boxShadow: 'none',
        },
      },
      // Glass card
      glassCard: {
        value: {
          background: 'glass.bg.subtle',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border',
          boxShadow: 'none',
          padding: '6',
        },
      },
      appShell: {
        value: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          height: '100dvh',
          position: 'relative',
          isolation: 'isolate',
          overflow: 'hidden',
          backgroundColor: 'app.bg',
          backgroundImage: appBackdropImage,
          backgroundSize: '100% 100%, 100% 100%',
          backgroundPosition: '0 0, 0 0',
          backgroundRepeat: 'no-repeat, no-repeat',
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
        },
      },
      appNavBar: {
        value: {
          background: 'ui.surface.card',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: '0',
          borderWidth: '0',
          borderBottomWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.divider',
          boxShadow: 'none',
        },
      },
      surfaceCard: {
        value: {
          background: 'ui.surface.card',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.default',
          boxShadow: 'var(--attune-shadow-floating, none)',
        },
      },
      surfacePanel: {
        value: {
          background: 'ui.surface.panel',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.default',
          boxShadow: 'none',
        },
      },
      surfaceRail: {
        value: {
          background: 'ui.surface.rail',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'none',
        },
      },
      surfacePopover: {
        value: {
          background: 'ui.surface.popover',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.default',
          boxShadow: 'none',
        },
      },
      drawerSurface: {
        value: {
          background: 'ui.surface.drawer',
          color: 'ui.text.primary',
          borderRadius: '0',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'none',
        },
      },
      drawerHeader: {
        value: {
          background: 'ui.surface.drawer',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'ui.border.subtle',
        },
      },
      drawerMuted: {
        value: {
          background: 'ui.surface.drawerMuted',
          borderColor: 'ui.border.subtle',
        },
      },
      drawerSection: {
        value: {
          background: 'ui.surface.drawer',
          borderRadius: '0',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'none',
        },
      },
      drawerInset: {
        value: {
          background: 'ui.surface.drawerMuted',
          borderRadius: '0',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'none',
        },
      },
      drawerRow: {
        value: {
          background: 'transparent',
          borderRadius: '0',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'none',
          transition: 'background-color 150ms ease, border-color 150ms ease',
          _hover: {
            background: 'ui.surface.selected',
            borderColor: 'ui.border.subtle',
          },
        },
      },
      surfaceMuted: {
        value: {
          background: 'ui.surface.input',
          borderRadius: 'glassInput',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
        },
      },
      surfaceInteractive: {
        value: {
          background: 'ui.surface.card',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.default',
          boxShadow: 'none',
          padding: '6',
          transition: 'background-color 160ms ease, border-color 160ms ease',
          _hover: {
            background: 'ui.surface.selected',
            borderColor: 'ui.border.subtle',
          },
          _active: {
            background: 'ui.surface.input',
          },
        },
      },
      surfaceSection: {
        value: {
          background: 'transparent',
          borderRadius: '0',
          borderWidth: '0',
          borderStyle: 'solid',
          borderColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      },
      surfaceInset: {
        value: {
          background: 'ui.surface.input',
          borderRadius: '0',
          borderWidth: '0',
          borderStyle: 'solid',
          borderColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      },
      dataTableSurface: {
        value: {
          background: 'ui.surface.card',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: '0',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'none',
          overflow: 'visible',
        },
      },
      metricCard: {
        value: {
          background: 'ui.surface.card',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          boxShadow: 'var(--attune-shadow-subtle, none)',
          padding: '4',
        },
      },
      filterPill: {
        value: {
          background: 'transparent',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          borderRadius: 'glassPill',
          color: 'ui.text.secondary',
          transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
          _hover: {
            background: 'ui.surface.input',
            borderColor: 'ui.border.default',
            color: 'ui.text.primary',
          },
        },
      },
      iconTile: {
        value: {
          background: 'ui.surface.input',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'ui.border.subtle',
          borderRadius: 'glassButton',
          color: 'ui.text.accent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      /**
       * Research assistant session + traces rails — paired chrome, quieter than `glassCard`
       * so both sides match each other and sit closer to the workspace grid.
       */
      glassRail: {
        value: {
          background: 'glass.bg.muted',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderRadius: 'glassCard',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border.subtle',
          boxShadow: 'none',
          padding: '6',
        },
      },
      // Interactive glass button
      glassButton: {
        value: {
          background: 'glass.bg.subtle',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: 'glassButton',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border.subtle',
          boxShadow: 'none',
          transition: controlTransition,
          _hover: {
            background: 'glass.bg',
            borderColor: 'glass.border',
          },
          _active: {
            transform: 'translateY(0px) scale(0.98)',
            boxShadow: 'none',
          },
        },
      },
      // Luminous accent glass (for primary actions)
      glassAccent: {
        value: {
          background: 'var(--attune-theme-accent-gradient)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: 'glassButton',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          boxShadow: 'none',
          color: 'accent.contrast',
          transition: controlTransition,
          _hover: {
            boxShadow: 'none',
            filter: 'brightness(1.03)',
          },
          _active: {
            transform: 'translateY(0px) scale(0.98)',
          },
        },
      },
      // Glass input field
      glassInput: {
        value: {
          background: 'glass.bg.muted',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderRadius: 'glassInput',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'glass.border.subtle',
          transition: controlTransition,
          _focusWithin: {
            borderColor: 'accent.solid',
            boxShadow: 'none',
          },
        },
      },
      // Soft app backdrop for translucent surfaces.
      glassMesh: {
        value: {
          backgroundColor: 'app.bg',
          backgroundImage: appBackdropImage,
          backgroundSize: '100% 100%, 100% 100%',
          backgroundPosition: '0 0, 0 0',
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundAttachment: 'fixed',
        },
      },
      // Dark glass for contrast
      glassDark: {
        value: {
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 'glass',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
          color: 'white',
        },
      },
      // Platform gradient badge (Claude only - others use icon images)
      platformClaude: {
        value: {
          background: 'linear-gradient(135deg, #E76F51 0%, #F4A261 100%)',
          borderRadius: 'glassInput',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: '600',
        },
      },
    },
    // Text styles
    textStyles: {
      glassHeading: {
        value: {
          fontWeight: '700',
          letterSpacing: '-0.015em',
          color: 'glass.text',
        },
      },
      glassBody: {
        value: {
          fontWeight: '400',
          letterSpacing: '-0.006em',
          lineHeight: '1.6',
          color: 'glass.text.secondary',
        },
      },
      glassBodyResponsive: {
        value: {
          fontSize: { base: 'sm', md: 'sm', lg: 'md' },
          lineHeight: { base: '1.55', md: '1.6' },
          color: 'glass.text.secondary',
        },
      },
      glassChatText: {
        value: {
          fontSize: { base: '13px', md: '14px', lg: '15px' },
          lineHeight: '1.65',
        },
      },
      glassCaption: {
        value: {
          fontWeight: '600',
          letterSpacing: '0.01em',
          fontSize: 'xs',
          lineHeight: '1.45',
          color: 'glass.text.secondary',
        },
      },
      sectionLabel: {
        value: {
          color: 'ui.text.tertiary',
          fontSize: 'xs',
          fontWeight: '600',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
      },
      metricLabel: {
        value: {
          color: 'ui.text.tertiary',
          fontSize: 'xs',
          fontWeight: '600',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
      },
    },
    slotRecipes: {
      dialog: dialogSlotRecipeAttune,
      drawer: drawerSlotRecipeAttune,
      menu: menuSlotRecipeAttune,
      nativeSelect: nativeSelectSlotRecipeAttune,
      select: selectSlotRecipeAttune,
      table: tableSlotRecipeAttune,
      tooltip: tooltipSlotRecipeAttune,
    },
    recipes: {
      button: buttonRecipeAttune,
      heading: headingRecipeBold,
      input: inputRecipeAttune,
      link: linkRecipeAttune,
      textarea: textareaRecipeAttune,
    },
  },
  globalCss: {
    html: {
      fontFamily:
        'var(--font-sans), "Manrope", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      textRendering: 'optimizeLegibility',
      WebkitTapHighlightColor: 'transparent',
    },
    body: {
      fontFeatureSettings: '"kern" 1, "liga" 1, "calt" 1',
      color: 'ui.text.primary',
      // Professional glass surface shared with the app shell.
      background: appBackdropImage,
      backgroundSize: '100% 100%, 100% 100%',
      backgroundAttachment: 'fixed',
      minHeight: '100dvh',
    },
    // Selection color
    '::selection': {
      background: 'var(--attune-selection-bg, rgba(23, 23, 23, 0.12))',
      color: 'inherit',
    },
    [interactiveControlSelectors]: {
      '--focus-ring-color': 'var(--attune-accent-focus-ring, currentColor)',
      '--focus-ring-offset': '1px',
      '--focus-ring-style': 'solid',
      '--focus-ring-width': '1px',
      touchAction: 'manipulation',
    },
    [interactiveControlFocusSelectors]: {
      outline: '1px solid var(--attune-accent-focus-ring, currentColor) !important',
      outlineOffset: '1px !important',
    },
    '[data-focus-style="inset"]:is(:focus-visible, [data-focus-visible])': {
      outline: 'none !important',
      outlineOffset: '0 !important',
      boxShadow: 'inset 0 0 0 1px var(--attune-accent-focus-ring, currentColor) !important',
    },
    // Scrollbar styling for glass aesthetic
    '::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      background: 'var(--attune-scrollbar-thumb, rgba(0, 0, 0, 0.15))',
      borderRadius: '9999px',
      border: '2px solid transparent',
      backgroundClip: 'content-box',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: 'var(--attune-scrollbar-thumb-hover, rgba(0, 0, 0, 0.25))',
      backgroundClip: 'content-box',
    },
    '*, *::before, *::after': {
      '@media (prefers-reduced-motion: reduce)': {
        scrollBehavior: 'auto !important',
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
        transitionDuration: '0.01ms !important',
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);
export default system;
